import type {
  FlexiForwardRfsGreeksDataState,
  FlexiForwardRfsMarginDataState,
  FlexiForwardRfsPremiumDataState,
  FxoHedge,
} from '@/store/flexi-forward/api/requestFlexiForwardRfs.model.ts';
import type { Trade, TradeCaptureValidationObject } from '@/store/types.ts';
import type { PredealCheck } from '@/types/common.ts';
import type { Currency, CurrencyPair } from '@/types/currency.ts';
import type { ISODate, Tenor } from '@/types/date.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';

export type FlexiForwardId = string;

export type FlexiForwardById = Record<FlexiForwardId, FxFlexiForward>;

export type FxFlexiForwardProductName = FxFlexiForward['type'];

export const DEFAULT_HEDGE_TYPE = 'Live';
export const POSSIBLE_HEDGE_TYPE = ['Live', 'Spot'] as const;
export type HedgeType = (typeof POSSIBLE_HEDGE_TYPE)[number];

export const DEFAULT_PRICE_TYPE = 'AMOUNT';
export const POSSIBLE_PRICE_TYPE = ['AMOUNT', 'PERCENT', 'PPS'] as const;
export type PriceType = (typeof POSSIBLE_PRICE_TYPE)[number];

export const DEFAULT_SOLVING_TYPE = 'zeroCost';
export const POSSIBLE_SOLVING_TYPE = ['zeroCost', 'solveRate', 'solveUpfront'] as const;
export type SolvingType = (typeof POSSIBLE_SOLVING_TYPE)[number];

export type CallabilityType = 'Windows' | 'American';

export type Solvable = 'ForwardRate' | 'PremiumPaymentAmount';

export const DEFAULT_CLIENTS_WAY = 'Buy';
export const POSSIBLE_CLIENTS_WAY = ['Buy', 'Sell'] as const;
export type ClientsWay = (typeof POSSIBLE_CLIENTS_WAY)[number];

export const DEFAULT_PREMIUM_WAY = 'Bid';
export const POSSIBLE_PREMIUM_WAY = ['Bid', 'Ask'] as const;
export type PremiumWay = (typeof POSSIBLE_PREMIUM_WAY)[number];

export const DEFAULT_MARGIN_TYPE = 'AMOUNT';
export const POSSIBLE_MARGIN_TYPE = ['AMOUNT', 'RATE'] as const;
export type MarginType = (typeof POSSIBLE_MARGIN_TYPE)[number];

export type FlexiForwardRfsStatus =
  | 'NOT_PRICEABLE'
  | 'PRICEABLE'
  | 'AWAITING_INDICATIVE'
  | 'AWAITING_EXECUTABLE'
  | 'STREAMING_INDICATIVE'
  | 'STREAMING_EXECUTABLE'
  | 'ERROR'
  | 'BYPASSABLE_ERROR';

interface StreamingError {
  multipassErrorCode: string;
  errorCode: string;
  errorMessage: string;
}

export type FlexiForwardQuote = {
  requestId: string;
  quoteId: string;
  quoteType: QuoteType;
  rfsWindow: number;
  predealChecks: PredealCheck[];
  frontTimestamp: number;
  backTimestamp: number;
  contribution: 'Automatic' | 'Manual';
  tiering: 'STREAMDEFAULT' | string;

  forwardRate: string;
  premiumData: FlexiForwardRfsPremiumDataState;
  greeks: FlexiForwardRfsGreeksDataState;
  marginData: FlexiForwardRfsMarginDataState;
  hedge?: FxoHedge;
};

export type FxFlexiForwardExecutionStatus = 'PENDING' | 'SUCCESS' | 'REJECTED';

export type QuoteType = 'Indicative' | 'Tradeable';

export type FlexiForwardRfs =
  | { status: 'AWAITING'; rfsId: string; quoteType: 'Indicative' | 'Tradeable'; traderId?: string }
  | {
      status: 'PRICING';
      rfsId: string;
      quote: FlexiForwardQuote;
      quoteId: string;
      quoteType: 'Indicative' | 'Tradeable';
      traderId?: string;
      initialRfsWindow: number;
    }
  | { status: 'EXPIRED'; quote: FlexiForwardQuote }
  | { status: 'CANCELLED' }
  | { status: 'ERROR'; error: StreamingError; rfsId?: string };

interface MifidRecord {
  date: string;
  isError?: boolean;
}

interface FlexiForwardMifid {
  rfsDate?: string;
  records: MifidRecord[];
}

export type FlexiForwardProperties = Partial<{
  tileId: string;

  isPriceable: boolean;

  currencyPair: CurrencyPair;
  currency1: Currency;
  currency2: Currency;

  clientsWay: ClientsWay;

  startDate: ISODate;
  startDateTenor: Tenor;

  callabilityType: CallabilityType;

  deliveryDate: ISODate;
  deliveryDateTenor: Tenor;

  execution: {
    status: FxFlexiForwardExecutionStatus;
  };

  expiryDate: ISODate;
  expiryDateTenor: Tenor;

  cutOffMarketPlace: string;
  possibleCutOffMarketPlaces: {
    default: string;
    possible: string[];
  };

  solvingType: SolvingType;
  solvable: Solvable;

  upfront: number | null;
  upfrontCurrency: string;

  rate: number | null;

  premiumDate: ISODate;
  premiumDateTenor: Tenor;
  premiumWay: PremiumWay;

  premiumType: PriceType;

  premiumPaymentAmountAskString: string;
  premiumPaymentAmountBidString: string;

  notional: number | null;
  notionalCurrencyIndex: CurrencyIndex;

  hedgeType: HedgeType;
  possibleHedgeTypes: HedgeType[];
  spotHedge: ISODate;
  deltaHedgeAmount: number | null;
  deltaHedgeCurrencyIndex: CurrencyIndex;
  spotRef: number | null;

  marginType: MarginType;
  marginCurrency: string;

  priceCurrencyIndex: CurrencyIndex;

  rfs: FlexiForwardRfs;
  rfsVersion: number;

  forceTradeable: boolean;
  executionId: string;

  maxDate: ISODate;

  mifid: FlexiForwardMifid;
}>;

export type FxFlexiForward = Trade<'FxFlexiForward', FlexiForwardProperties, FlexiForwardTradeCaptureError>;

export type FlexiForwardTradeCaptureError = TradeCaptureValidationObject<FlexiForwardProperties>;
